import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
// next
import NextLink from 'next/link';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
// config
import { EVENT_ID, INPUT_ID } from '../../../config';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login } = useAuth();

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().trim().email('이메일을 입력하세요').required('이메일을 입력하세요'),
    password: Yup.string().trim().required('비밀번호를 입력하세요'),
  });

  const defaultValues = {
    email: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      await login(data.email, data.password);
      enqueueSnackbar('로그인 하였습니다')
    } catch (error) {
      if (isMountedRef.current) {
        if (error.response && error.response.status === 401) {
          setError('afterSubmit', { message: '이메일과 비밀번호를 확인해주세요' });
        } else {
          reset();
          setError('afterSubmit', { message: '로그인에 실패하였습니다. 관리자에게 문의해주세요' });
        }
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField name="email" label="이메일" autoComplete="username" id={INPUT_ID.loginEmail} />

        <RHFTextField
          name="password"
          label="비밀번호"
          id={INPUT_ID.loginPassword}
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <div />
        <NextLink href={PATH_AUTH.resetPassword} passHref>
          <Link variant="subtitle2">비밀번호를 잊어버렸나요?</Link>
        </NextLink>
      </Stack>

      <LoadingButton id={EVENT_ID.bizLogin} fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
        로그인 하기
      </LoadingButton>
    </FormProvider>
  );
}
